import { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { ApiNotification } from 'api/notification/types/ApiNotification';
import { Box } from 'components/Box';
import { Icon } from 'components/Icon';
import { Notifications } from 'components/Notifications';
import { PageHeaderDropdownMenu } from 'components/Page/Header/PageHeaderDropdownMenu';
import {
  fetchNotifications,
  markNotificationsAsViewed,
} from 'modules/main/store/ducks/userNotifications.actions';
import { useMainDispatch } from 'modules/main/store/hooks/useMainDispatch';
import { useMainSelector } from 'modules/main/store/hooks/useMainSelector';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import { colors } from 'theme/theme';
import { MappedNotification } from 'types/mappedNotifications/MappedNotification';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { isBrowser } from 'utils/browser';
import { getMappedNotificationIds } from 'utils/userNotifications';

const Tag = styled.div`
  z-index: 1;
  position: absolute;
  right: -2px;
  top: -2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${colors.decorativePoppy};
  font-size: 12px;
  color: ${colors.white};
`;

export function PageHeaderNotificationsDropdown() {
  const { user } = useUserEnvironment();

  const dispatch = useMainDispatch();

  const { notifications, isLoaded } = useMainSelector(
    (state) => state.userNotifications,
  );

  const unseenNotifications = useMemo(
    () => notifications.filter((notification) => !notification.viewed),
    [notifications],
  );

  const showBadge = useMemo(
    () => isBrowser() && unseenNotifications.length > 0,
    [unseenNotifications],
  );

  const handleView = useCallback(
    (viewedNotifications: (ApiNotification | MappedNotification)[]) =>
      dispatch(
        markNotificationsAsViewed(
          viewedNotifications.flatMap((notification) =>
            getMappedNotificationIds(notification),
          ),
        ),
      ),
    [dispatch],
  );

  useEffect(() => {
    if (user?.hasNotifications && !isLoaded) {
      dispatch(fetchNotifications());
    }
  }, [user?.hasNotifications, dispatch, isLoaded]);

  return (
    <PageHeaderDropdownMenu
      right
      aria-label={getText('Notifications')}
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/no-unstable-nested-components
      title={() => (
        <Box position="relative">
          {showBadge ? (
            <Tag>
              {unseenNotifications.length > 99 ? (
                <>&infin;</>
              ) : (
                unseenNotifications.length
              )}
            </Tag>
          ) : null}
          <Box mr="6px">
            <Icon name="bell-2" size={22} />
          </Box>
        </Box>
      )}
      height="370px"
      width={260}
      data-qa-id="notifications-dropdown"
      onClick={(open) => {
        trackClicked('Header Utility CTA - Notifications');

        if (open && notifications.length) {
          handleView(notifications);

          trackEvent('Displayed User Menu Notifications', {
            notification_types: notifications.reduce(
              (acc, notification) =>
                // @ts-expect-error TS(2769): No overload matches this call.
                acc.includes(notification.type)
                  ? acc
                  : [...acc, notification.type],
              [],
            ),
          });
        }
      }}
    >
      {({ closeMenu }) => (
        <Notifications notifications={notifications} onClick={closeMenu} />
      )}
    </PageHeaderDropdownMenu>
  );
}
